import { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { M_TEAMS, M_ROSTERS } from '../data/matchData';

/* ---- Flag ---- */
function Flag({ c, w = 17 }) {
  const h = Math.round(w * 0.66);
  if (c === 'ru') {
    return (
      <span style={{ display: 'inline-block', width: w, height: h, borderRadius: 2, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.18)', flexShrink: 0,
        background: 'linear-gradient(#fff 0 33.3%, #0039A6 33.3% 66.6%, #D52B1E 66.6% 100%)' }} />
    );
  }
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: w, height: h, borderRadius: 2,
      overflow: 'hidden', border: '1px solid rgba(255,255,255,0.18)', flexShrink: 0, background: '#00AFCA' }}>
      <span style={{ position: 'absolute', top: '50%', left: '52%', width: h * 0.42, height: h * 0.42,
        transform: 'translate(-50%,-50%)', borderRadius: '50%', background: '#FFD24A',
        boxShadow: '0 0 0 1.5px rgba(255,210,74,0.5)' }} />
      <span style={{ position: 'absolute', left: 2, top: 1, bottom: 1, width: 2.5, background: '#FFD24A', borderRadius: 2 }} />
    </span>
  );
}

/* ---- Crest ---- */
export function Crest({ tag, size = 44, live }) {
  const t = M_TEAMS[tag] || { color: 'var(--accent)', tag };
  return (
    <div style={{
      position: 'relative', width: size, height: size, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: size * 0.34, letterSpacing: '0.02em',
      color: '#fff', clipPath: 'polygon(50% 0, 100% 26%, 100% 74%, 50% 100%, 0 74%, 0 26%)',
      background: `linear-gradient(150deg, ${t.color}, ${t.color}55 70%, var(--surface-3))`,
      boxShadow: `inset 0 0 0 1.5px ${t.color}88${live ? `, 0 0 18px -2px ${t.color}aa` : ''}`,
    }}>
      <span style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>{t.tag}</span>
    </div>
  );
}

/* ---- Win type icon ---- */
function WinIcon({ type, color }) {
  const p = { width: 11, height: 11, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'bomb') return <svg {...p} fill={color} stroke="none"><circle cx="11" cy="14" r="6.5" /><path d="M16 9l3-3 2 2" stroke={color} strokeWidth="2.4" fill="none" /></svg>;
  if (type === 'defuse') return <svg {...p}><path d="M5 12h14M9 8l-4 4 4 4M15 8l4 4-4 4" /></svg>;
  if (type === 'time') return <svg {...p}><circle cx="12" cy="12" r="8" /><path d="M12 8v4l2.5 2" /></svg>;
  return <svg {...p}><circle cx="12" cy="12" r="3.2" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /></svg>;
}

/* ---- Round history ---- */
function RoundHistory({ rounds, tagA, tagB, animate }) {
  const CT = '#5C8DFF', T = '#E6B73C';
  const [shown, setShown] = useState(animate ? 0 : (rounds ? rounds.length : 0));
  useEffect(() => {
    if (!animate || !rounds) return;
    setShown(0);
    let i = 0;
    const id = setInterval(() => { i++; setShown(i); if (i >= rounds.length) clearInterval(id); }, 28);
    return () => clearInterval(id);
  }, [rounds, animate]);
  if (!rounds || !rounds.length) return null;

  let a = 0, b = 0;
  const cells = rounds.map((r, i) => {
    if (r.w === 'A') a++; else b++;
    return { ...r, a, b, idx: i };
  });

  const Cell = ({ r, lane }) => {
    const mine = r.w === lane;
    if (!mine) return <div style={{ width: 19, height: 19 }} />;
    const col = r.side === 'ct' ? CT : T;
    const visible = r.idx < shown;
    return (
      <div title={`Раунд ${r.idx + 1}`} style={{
        width: 19, height: 19, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 4, background: col, boxShadow: `0 0 10px -3px ${col}`,
        opacity: visible ? 1 : 0, transform: visible ? 'scale(1)' : 'scale(0.4)',
        transition: 'opacity .25s, transform .25s var(--ease-spring)',
      }}><WinIcon type={r.type} color="#0b0b0d" /></div>
    );
  };

  const half = 12;
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, minWidth: '100%' }}>
        <div className="row" style={{ gap: 3, alignItems: 'center' }}>
          <span className="mono" style={{ width: 38, fontSize: 11, color: 'var(--fg-soft)', fontWeight: 700, textAlign: 'right', paddingRight: 6 }}>{tagA}</span>
          {cells.map((r, i) => (
            <span key={i} style={{ display: 'contents' }}>
              {i === half && <div style={{ width: 2, height: 19, background: 'var(--line-strong)', margin: '0 3px', borderRadius: 2 }} />}
              <Cell r={r} lane="A" />
            </span>
          ))}
        </div>
        <div className="row" style={{ gap: 3, alignItems: 'center' }}>
          <span style={{ width: 38 }} />
          {cells.map((r, i) => (
            <span key={i} style={{ display: 'contents' }}>
              {i === half && <div style={{ width: 2, margin: '0 3px' }} />}
              <span className="mono" style={{ width: 19, fontSize: 8.5, textAlign: 'center', color: r.idx < shown ? (r.w === 'A' ? '#5C8DFF' : '#E6B73C') : 'transparent', lineHeight: 1 }}>{r.w === 'A' ? r.a : r.b}</span>
            </span>
          ))}
        </div>
        <div className="row" style={{ gap: 3, alignItems: 'center' }}>
          <span className="mono" style={{ width: 38, fontSize: 11, color: 'var(--fg-soft)', fontWeight: 700, textAlign: 'right', paddingRight: 6 }}>{tagB}</span>
          {cells.map((r, i) => (
            <span key={i} style={{ display: 'contents' }}>
              {i === half && <div style={{ width: 2, height: 19, background: 'var(--line-strong)', margin: '0 3px', borderRadius: 2 }} />}
              <Cell r={r} lane="B" />
            </span>
          ))}
        </div>
      </div>
      <div className="row gap-4" style={{ marginTop: 12, flexWrap: 'wrap' }}>
        <span className="row gap-1" style={{ alignItems: 'center', fontSize: 11, color: 'var(--fg-muted)' }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: CT }} /> Победа за CT</span>
        <span className="row gap-1" style={{ alignItems: 'center', fontSize: 11, color: 'var(--fg-muted)' }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: T }} /> Победа за T</span>
        <span className="row gap-1" style={{ alignItems: 'center', fontSize: 11, color: 'var(--fg-muted)' }}><WinIcon type="bomb" color="var(--fg-muted)" /> подрыв</span>
        <span className="row gap-1" style={{ alignItems: 'center', fontSize: 11, color: 'var(--fg-muted)' }}><WinIcon type="defuse" color="var(--fg-muted)" /> разминир.</span>
        <span className="row gap-1" style={{ alignItems: 'center', fontSize: 11, color: 'var(--fg-muted)' }}><WinIcon type="time" color="var(--fg-muted)" /> время</span>
      </div>
    </div>
  );
}

/* ---- Rating color ---- */
function ratingColor(r) {
  if (r >= 1.15) return '#4ADE80';
  if (r >= 1.0) return '#9FE08A';
  if (r >= 0.85) return '#E6B73C';
  return '#FF6B6B';
}

/* ---- Scoreboard ---- */
function Scoreboard({ board, tag, won, motion }) {
  const t = M_TEAMS[tag];
  const top = board[0];
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div className="row" style={{ alignItems: 'center', gap: 10, padding: '11px 14px',
        background: `linear-gradient(90deg, ${t.color}22, transparent)`, borderBottom: '1px solid var(--line)' }}>
        <Crest tag={tag} size={26} />
        <span className="display" style={{ fontSize: 16 }}>{t.name}</span>
        {won && <span className="mono" style={{ marginLeft: 6, fontSize: 9.5, letterSpacing: '0.14em', color: '#0b0b0d',
          background: 'var(--gold)', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>ПОБЕДА</span>}
        <span className="grow" />
        <span className="mono col-head" style={{ width: 52, textAlign: 'center' }}>K–D</span>
        <span className="mono col-head" style={{ width: 36, textAlign: 'center' }}>+/–</span>
        <span className="mono col-head" style={{ width: 44, textAlign: 'center' }}>ADR</span>
        <span className="mono col-head kast" style={{ width: 44, textAlign: 'center' }}>KAST</span>
        <span className="mono col-head hsc" style={{ width: 40, textAlign: 'center' }}>HS%</span>
        <span className="mono col-head" style={{ width: 54, textAlign: 'center' }}>RATING</span>
      </div>
      {board.map((p, i) => {
        const rc = ratingColor(p.rating);
        const isTop = p.nick === top.nick;
        return (
          <div key={p.nick} className="sb-row" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
            borderBottom: i < board.length - 1 ? '1px solid var(--line)' : 'none',
            animation: `fadeUp ${0.4 / (motion || 1)}s var(--ease-out) both`, animationDelay: `${i * 0.05}s`,
            background: isTop ? 'rgba(230,183,60,0.05)' : 'transparent',
          }}>
            <div className="row gap-2" style={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
              <div className="center" style={{ width: 30, height: 30, borderRadius: 6, flexShrink: 0,
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
                background: `linear-gradient(140deg, ${t.color}55, var(--surface-3))`,
                border: '1px solid var(--line)', color: '#fff' }}>
                {p.nick.slice(0, 2).toUpperCase()}</div>
              <div className="col" style={{ minWidth: 0, flex: 1 }}>
                <span className="row gap-1" style={{ alignItems: 'center' }}>
                  <Flag c={p.c} w={15} />
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nick}</span>
                  {isTop && <svg width="12" height="12" viewBox="0 0 24 24" fill="var(--gold)" style={{ flexShrink: 0 }}><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" /></svg>}
                </span>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.real}</span>
              </div>
            </div>
            <span className="mono sb-stat" style={{ width: 52, textAlign: 'center' }}>
              <span style={{ color: 'var(--fg)' }}>{p.k}</span><span style={{ color: 'var(--fg-faint)' }}> – {p.d}</span></span>
            <span className="mono sb-stat" style={{ width: 36, textAlign: 'center', color: p.plus > 0 ? '#4ADE80' : p.plus < 0 ? '#FF6B6B' : 'var(--fg-muted)', fontWeight: 700 }}>{p.plus > 0 ? '+' : ''}{p.plus}</span>
            <span className="mono sb-stat" style={{ width: 44, textAlign: 'center', color: 'var(--fg-soft)' }}>{p.adr}</span>
            <span className="mono sb-stat kast" style={{ width: 44, textAlign: 'center', color: 'var(--fg-soft)' }}>{p.kast}%</span>
            <span className="mono sb-stat hsc" style={{ width: 40, textAlign: 'center', color: 'var(--fg-muted)' }}>{p.hs}%</span>
            <span style={{ width: 54, position: 'relative', textAlign: 'center' }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: rc, position: 'relative', zIndex: 1 }}>{p.rating.toFixed(2)}</span>
              <span style={{ position: 'absolute', left: 0, right: 0, bottom: -5, height: 3, borderRadius: 2, background: 'var(--surface-3)' }}>
                <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, (p.rating / 1.8) * 100)}%`, background: rc, borderRadius: 2, boxShadow: `0 0 6px ${rc}` }} /></span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---- Veto strip ---- */
function VetoStrip({ veto }) {
  if (!veto) return null;
  return (
    <div className="row wrap gap-2" style={{ alignItems: 'center' }}>
      {veto.map((v, i) => {
        const pick = v.act === 'pick', dec = v.act === 'decider';
        const t = M_TEAMS[v.team];
        return (
          <span key={i} className="mono" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '6px 10px', borderRadius: 'var(--r-sm)',
            background: pick ? 'rgba(74,222,128,0.10)' : dec ? 'rgba(230,183,60,0.12)' : 'var(--surface-2)',
            border: `1px solid ${pick ? 'rgba(74,222,128,0.4)' : dec ? 'rgba(230,183,60,0.4)' : 'var(--line)'}`,
            color: pick ? '#9FE08A' : dec ? 'var(--gold)' : 'var(--fg-muted)',
          }}>
            <span style={{ fontWeight: 700, color: t ? t.color : 'var(--fg-soft)' }}>{dec ? '◆' : (t ? t.tag : v.team)}</span>
            {dec ? 'DECIDER' : pick ? 'PICK' : 'BAN'}
            <span style={{ color: 'var(--fg-soft)', fontWeight: 600 }}>{v.map}</span>
          </span>
        );
      })}
    </div>
  );
}

/* ---- Helpers ---- */
function BlockTitle({ children }) {
  return (
    <div className="row gap-2" style={{ alignItems: 'center', marginBottom: 14 }}>
      <span style={{ width: 14, height: 2, background: 'var(--accent)' }} />
      <span className="mono" style={{ fontSize: 11, letterSpacing: '0.2em', color: 'var(--fg-soft)', textTransform: 'uppercase' }}>{children}</span>
    </div>
  );
}

function LivePill() {
  return (
    <span className="row gap-2" style={{ alignItems: 'center', padding: '5px 11px', borderRadius: 'var(--r-sm)',
      background: 'rgba(225,18,23,0.14)', border: '1px solid var(--line-accent)' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-bright)',
        boxShadow: '0 0 10px var(--accent)', animation: 'c4blink 1s steps(1) infinite' }} />
      <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.16em', color: 'var(--accent-bright)', fontWeight: 700 }}>LIVE</span>
    </span>
  );
}

function TeamHead({ tag, align, won }) {
  const t = M_TEAMS[tag];
  return (
    <div className="row" style={{ alignItems: 'center', gap: 14, flexDirection: align === 'right' ? 'row' : 'row-reverse', minWidth: 0 }}>
      <div className="col" style={{ alignItems: align === 'right' ? 'flex-end' : 'flex-start', minWidth: 0 }}>
        <span className="display" style={{ fontSize: 'clamp(18px,2.4vw,26px)', color: won ? 'var(--gold)' : 'var(--fg)', whiteSpace: 'nowrap' }}>{t.name}</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)', letterSpacing: '0.1em' }}>SEED #{t.seed} · {t.tag}</span>
      </div>
      <Crest tag={tag} size={56} live={won} />
    </div>
  );
}

function SideTag({ tag, side }) {
  const col = side === 'CT' ? '#5C8DFF' : '#E6B73C';
  return (
    <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '5px 9px', borderRadius: 6,
      background: `${col}1f`, border: `1px solid ${col}55`, color: col, fontWeight: 700 }}>
      <span style={{ width: 7, height: 7, borderRadius: 2, background: col }} />{tag} · {side}</span>
  );
}

function LiveMapStrip({ map, tagA, tagB, motion }) {
  const lv = map.live;
  const [clock, setClock] = useState(lv.clock);
  const [bomb, setBomb] = useState(lv.bomb);
  useEffect(() => {
    const id = setInterval(() => {
      setClock(c => { if (c <= 1) { setBomb(b => !b); return bomb ? 40 : 115; } return c - 1; });
    }, 1000);
    return () => clearInterval(id);
  }, [bomb]);
  const mm = Math.floor(clock / 60), ss = clock % 60;
  const ctTag = lv.ctTeam === 'A' ? tagA : tagB;
  const tTag = lv.ctTeam === 'A' ? tagB : tagA;
  return (
    <div className="row wrap" style={{ alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: '14px 18px', marginBottom: 24,
      background: 'linear-gradient(120deg, rgba(225,18,23,0.10), var(--surface-2))', border: '1px solid var(--line-accent)', borderRadius: 'var(--r-md)' }}>
      <div className="row gap-3" style={{ alignItems: 'center' }}>
        <span className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--fg-muted)' }}>РАУНД {lv.round}</span>
        <span className="display mono" style={{ fontSize: 28, color: bomb ? 'var(--accent-bright)' : 'var(--fg)', minWidth: 78, textShadow: bomb ? '0 0 14px var(--accent-glow)' : 'none' }}>
          {bomb ? '0:' + String(Math.min(40, clock)).padStart(2, '0') : `${mm}:${String(ss).padStart(2, '0')}`}</span>
        {bomb && (
          <span className="row gap-2" style={{ alignItems: 'center' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--accent-bright)', boxShadow: '0 0 10px var(--accent)', animation: 'c4blink .5s steps(1) infinite' }} />
            <span className="mono" style={{ fontSize: 11, color: 'var(--accent-bright)', fontWeight: 700, letterSpacing: '0.1em' }}>БОМБА УСТАНОВЛЕНА</span>
          </span>
        )}
      </div>
      <div className="row gap-3" style={{ alignItems: 'center' }}>
        <SideTag tag={ctTag} side="CT" />
        <SideTag tag={tTag} side="T" />
      </div>
    </div>
  );
}

function FormCol({ tag, align }) {
  const t = M_TEAMS[tag];
  const form = ['W', 'W', 'L', 'W', 'W'];
  const roster = M_ROSTERS[tag] || M_ROSTERS.AML;
  return (
    <div className="col" style={{ gap: 12, alignItems: align === 'right' ? 'flex-end' : 'flex-start' }}>
      <div className="row gap-2" style={{ alignItems: 'center', flexDirection: align === 'right' ? 'row' : 'row-reverse' }}>
        <Crest tag={tag} size={36} />
        <span className="display" style={{ fontSize: 18 }}>{t.name}</span>
      </div>
      <div className="row gap-1" style={{ flexDirection: align === 'right' ? 'row' : 'row-reverse' }}>
        {form.map((f, i) => (
          <span key={i} className="mono center" style={{ width: 22, height: 22, borderRadius: 5, fontSize: 11, fontWeight: 700,
            background: f === 'W' ? 'rgba(74,222,128,0.15)' : 'rgba(255,107,107,0.15)',
            color: f === 'W' ? '#4ADE80' : '#FF6B6B',
            border: `1px solid ${f === 'W' ? 'rgba(74,222,128,0.4)' : 'rgba(255,107,107,0.4)'}` }}>{f}</span>
        ))}
      </div>
      <div className="col gap-1" style={{ alignItems: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {roster.map(p => (
          <span key={p.nick} className="row gap-1" style={{ alignItems: 'center', fontSize: 12, color: 'var(--fg-soft)', flexDirection: align === 'right' ? 'row' : 'row-reverse' }}>
            <Flag c={p.c} w={14} /><span style={{ fontWeight: 600 }}>{p.nick}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function UpcomingBody({ match }) {
  return (
    <div className="col" style={{ gap: 18 }}>
      <BlockTitle>Прогноз · форма команд</BlockTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 18, alignItems: 'center' }}>
        <FormCol tag={match.A} align="right" />
        <span className="display" style={{ fontSize: 22, color: 'var(--fg-faint)' }}>VS</span>
        <FormCol tag={match.B} align="left" />
      </div>
      <p style={{ textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13, margin: '8px 0 0' }}>
        {match.tbd ? 'Соперник определится по итогам полуфиналов.' : 'Составы заявлены. Карты будут выбраны на стадии вето перед матчем.'}
      </p>
    </div>
  );
}

/* ---- Main export ---- */
export default function MatchDetail({ match, onClose, motion }) {
  const A = M_TEAMS[match.A], B = M_TEAMS[match.B];
  const playableMaps = match.maps ? match.maps.filter(m => m.status !== 'upcoming') : [];
  const liveIdx = match.maps ? match.maps.findIndex(m => m.status === 'live') : -1;
  const [tab, setTab] = useState(liveIdx >= 0 ? liveIdx : (playableMaps.length ? match.maps.indexOf(playableMaps[playableMaps.length - 1]) : 0));
  const map = match.maps ? match.maps[tab] : null;

  return (
    <Modal open onClose={onClose} width={1080} noHandle>
      <div style={{ position: 'relative' }}>
        {/* close */}
        <button onClick={onClose} className="md-close" style={{ position: 'absolute', top: 16, right: 16, zIndex: 5,
          width: 38, height: 38, borderRadius: 8, background: 'var(--surface-3)', border: '1px solid var(--line-strong)',
          color: 'var(--fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>

        {/* header */}
        <div style={{ position: 'relative', overflow: 'hidden', padding: '26px 28px 22px',
          background: `linear-gradient(120deg, ${A.color}1f, transparent 42%, ${B.color}1f), linear-gradient(160deg, var(--surface-2), var(--surface))`,
          borderBottom: '1px solid var(--line)' }}>
          <div className="row gap-3" style={{ alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
            <span className="eyebrow">{match.event}</span>
            <span style={{ color: 'var(--fg-faint)' }}>·</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', letterSpacing: '0.12em' }}>{match.stage} · BO{match.bo}</span>
            <span className="row gap-1" style={{ marginLeft: 2 }}>
              {Array.from({ length: match.stars }).map((_, i) => (
                <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill="var(--gold)"><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" /></svg>
              ))}
            </span>
            {match.status === 'live' && <LivePill />}
          </div>
          <div className="row" style={{ alignItems: 'center', justifyContent: 'center', gap: 'clamp(16px,4vw,48px)' }}>
            <TeamHead tag={match.A} align="right" won={match.mapsWon.A > match.mapsWon.B && match.status === 'finished'} />
            <div className="col" style={{ alignItems: 'center', gap: 4 }}>
              <div className="row" style={{ alignItems: 'center', gap: 14 }}>
                <span className="display md-score" style={{ color: match.mapsWon.A >= match.mapsWon.B ? 'var(--fg)' : 'var(--fg-muted)' }}>{match.mapsWon.A}</span>
                <span className="display" style={{ fontSize: 26, color: 'var(--fg-faint)' }}>:</span>
                <span className="display md-score" style={{ color: match.mapsWon.B >= match.mapsWon.A ? 'var(--fg)' : 'var(--fg-muted)' }}>{match.mapsWon.B}</span>
              </div>
              <span className="mono" style={{ fontSize: 10.5, letterSpacing: '0.18em', color: match.status === 'live' ? 'var(--accent-bright)' : 'var(--fg-faint)' }}>
                {match.status === 'live' ? 'ИДЁТ ИГРА' : match.status === 'finished' ? 'ЗАВЕРШЁН' : (match.time || 'СКОРО')}</span>
            </div>
            <TeamHead tag={match.B} align="left" won={match.mapsWon.B > match.mapsWon.A && match.status === 'finished'} />
          </div>
        </div>

        {/* map tabs */}
        {match.maps && (
          <div className="row" style={{ gap: 0, borderBottom: '1px solid var(--line)', overflowX: 'auto', background: 'var(--bg-deep)' }}>
            {match.maps.map((m, i) => {
              const active = i === tab, up = m.status === 'upcoming', live = m.status === 'live';
              return (
                <button key={i} onClick={() => !up && setTab(i)} style={{
                  position: 'relative', flex: '1 0 auto', minWidth: 150, padding: '13px 18px',
                  cursor: up ? 'default' : 'pointer', background: active ? 'var(--surface-2)' : 'transparent',
                  border: 'none', borderRight: '1px solid var(--line)', textAlign: 'left', opacity: up ? 0.5 : 1, transition: 'background .2s' }}>
                  <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span className="col" style={{ gap: 2 }}>
                      <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.14em', color: live ? 'var(--accent-bright)' : 'var(--fg-faint)' }}>{live ? '● LIVE' : up ? 'СЛЕД.' : `КАРТА ${i + 1}`}</span>
                      <span className="display" style={{ fontSize: 16 }}>{m.name}</span>
                    </span>
                    {!up && <span className="mono" style={{ fontSize: 15, fontWeight: 700 }}>
                      <span style={{ color: m.a >= m.b ? 'var(--fg)' : 'var(--fg-muted)' }}>{m.a}</span>
                      <span style={{ color: 'var(--fg-faint)' }}>:</span>
                      <span style={{ color: m.b >= m.a ? 'var(--fg)' : 'var(--fg-muted)' }}>{m.b}</span></span>}
                  </div>
                  {active && <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: live ? 'var(--accent)' : 'var(--gold)', boxShadow: live ? '0 0 8px var(--accent-glow)' : 'none' }} />}
                </button>
              );
            })}
          </div>
        )}

        {/* body */}
        <div style={{ padding: '24px 28px 28px' }}>
          {map && map.live && <LiveMapStrip map={map} tagA={match.A} tagB={match.B} motion={motion} />}

          {map && map.rounds && (
            <div style={{ marginBottom: 26 }}>
              <BlockTitle>История раундов</BlockTitle>
              <RoundHistory rounds={map.rounds} tagA={match.A} tagB={match.B} animate />
            </div>
          )}

          {map && map.board ? (
            <div className="col" style={{ gap: 16 }}>
              <BlockTitle>Статистика игроков · {map.name}</BlockTitle>
              <Scoreboard board={map.board.A} tag={match.A} won={map.a > map.b} motion={motion} />
              <Scoreboard board={map.board.B} tag={match.B} won={map.b > map.a} motion={motion} />
            </div>
          ) : (
            <UpcomingBody match={match} />
          )}

          {match.veto && (
            <div style={{ marginTop: 26 }}>
              <BlockTitle>Пики и баны</BlockTitle>
              <VetoStrip veto={match.veto} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
